// Extracted from original/src/App.tsx:13077-13463 (the "menu_customization" settings card, ADR-08 §2.1):
// card frame (:13079-13087), "sidebar order" list with the icon-edit button (:13089-13241), "settings
// sections order" list (:13242-13380) and "dashboard cards order" list (:13381-13460). Row markup is
// verbatim (:13139-13162 / :13299-13311 / :13410-13422); the move buttons keep their disabled styling
// (:13192, :13225, :13334, :13360, :13436, :13455).
// Cut: the label switches (:13114-13138, :13261-13297, :13386-13409) — labels arrive resolved from the
// container (D-124: every label, only permitted items); the swap logic over `sidebarOrder` /
// `settingsOrder` / `dashboardOrder` state and localStorage — the container swaps and writes the
// `ui.*_order` preference (D-123); `setEditingIconKey` / `setShowIconPicker` → `onEditIcon` (B4, D-124).
import { ChevronDown, ChevronUp, Grip, LayoutDashboard, Palette, Settings } from 'lucide-react';
import { useT } from '../i18n-context.js';
import { SettingsCardFrame } from './Settings.js';

export interface OrderListItem {
  readonly key: string;
  readonly label: string;
}

export interface OrderList {
  readonly items: readonly OrderListItem[];
  /** Swap the item at `index` with the previous one (already guarded for index 0). */
  readonly onMoveUp: (index: number) => void;
  /** Swap the item at `index` with the next one (already guarded for the last index). */
  readonly onMoveDown: (index: number) => void;
}

export interface MenuCustomizationCardProps {
  readonly customIcon?: string;
  readonly sidebar: OrderList;
  /** Sidebar rows only (App.tsx:13165-13175): opens the icon picker for that section. */
  readonly onEditIcon?: (key: string) => void;
  readonly settings: OrderList;
  readonly dashboard: OrderList;
}

const ROW_CLASS =
  "flex items-center justify-between p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d] group hover:border-blue-200 dark:hover:border-blue-900/50 transition-all";
const HEADING_CLASS =
  "text-xs font-black text-gray-400 dark:text-[#8b949e] mb-4 uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d] pb-2 flex items-center gap-2";
const moveClass = (disabled: boolean) =>
  `p-1.5 rounded-lg transition-all ${disabled ? "text-gray-200 dark:text-gray-800" : "text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"}`;

export function MenuCustomizationCard(props: MenuCustomizationCardProps) {
  const t = useT();

  const rows = (list: OrderList, listId: string, editIcon?: (key: string) => void) =>
    list.items.map(({ key, label }, index) => (
      <div key={key} className={ROW_CLASS} data-testid={`order-${listId}-${key}`}>
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-white dark:bg-[#0d1117] rounded-lg text-gray-400">
            <Grip size={14} />
          </div>
          <span className="text-xs font-bold text-gray-700 dark:text-[#f0f6fc] uppercase tracking-tight">
            {label}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {editIcon && (
            <button
              onClick={() => editIcon(key)}
              className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
              title={t('editIcon')}
            >
              <Palette size={14} />
            </button>
          )}
          <button
            disabled={index === 0}
            onClick={() => {
              if (index === 0) return;
              list.onMoveUp(index);
            }}
            className={moveClass(index === 0)}
            title={t('moveUp')}
          >
            <ChevronUp size={14} />
          </button>
          <button
            disabled={index === list.items.length - 1}
            onClick={() => {
              if (index === list.items.length - 1) return;
              list.onMoveDown(index);
            }}
            className={moveClass(index === list.items.length - 1)}
            title={t('moveDown')}
          >
            <ChevronDown size={14} />
          </button>
        </div>
      </div>
    ));

  return (
    <SettingsCardFrame cardKey="menu_customization" {...(props.customIcon ? { customIcon: props.customIcon } : {})}>
      <div className="space-y-8">
        {/* Sidebar Order */}
        <div>
          <h4 className={HEADING_CLASS}>
            <LayoutDashboard size={14} />
            {t('sidebarOrder')}
          </h4>
          <div className="space-y-2">
            {rows(props.sidebar, "sidebar", props.onEditIcon)}
          </div>
        </div>
        {/* Settings Order */}
        <div>
          <h4 className={HEADING_CLASS}>
            <Settings size={14} />
            {t('settingsOrder')}
          </h4>
          <div className="space-y-2">
            {rows(props.settings, "settings")}
          </div>
        </div>
        {/* Dashboard Order */}
        <div>
          <h4 className={HEADING_CLASS}>
            <LayoutDashboard size={14} />
            {t('dashboardOrder')}
          </h4>
          <div className="space-y-2">
            {rows(props.dashboard, "dashboard")}
          </div>
        </div>
      </div>
    </SettingsCardFrame>
  );
}
